// src/components/dashboard/DashboardFilters.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export default function DashboardFilters({
  centers = [],
  value,
  onChange,

  // NUEVO: mapa code(normalizado) -> name (viene de DashboardPage)
  centerNameByCode = {},

  // NUEVO: normalizador (viene de DashboardPage)
  normalizeCenterCode = (code) => {
    if (code == null) return "";
    const s = String(code).trim();
    if (/^\d+$/.test(s)) return s.length < 8 ? s.padStart(8, "0") : s;
    return s;
  },
}) {
  const { t } = useTranslation();

  const [local, setLocal] = useState(value);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => setLocal(value), [value]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!boxRef.current) return;
      if (!boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const update = (patch) => {
    const next = { ...local, ...patch };
    setLocal(next);
    onChange(next);
  };

  const toggleCenter = (code) => {
    const has = local.centerCodes.includes(code);
    const next = has
      ? local.centerCodes.filter((c) => c !== code)
      : [...local.centerCodes, code];
    update({ centerCodes: next });
  };

  // Helper: nombre visible para un centerCode de dashboard (c.centerCode)
  const getCenterLabel = (centerCode) => {
    const norm = normalizeCenterCode(centerCode);
    return centerNameByCode[norm] || centerNameByCode[String(centerCode)] || norm;
  };

  const selectedCenters = useMemo(() => {
    const set = new Set(local.centerCodes || []);
    return centers.filter((c) => set.has(c.centerCode));
  }, [centers, local.centerCodes]);

  const filteredCenters = useMemo(() => {
    const needle = (q || "").trim().toLowerCase();
    if (!needle) return centers;

    return centers.filter((c) => {
      const codeRaw = String(c.centerCode || "");
      const codeNorm = normalizeCenterCode(c.centerCode);
      const name = getCenterLabel(c.centerCode);

      const nameLc = String(name || "").toLowerCase();
      const codeLc = codeRaw.toLowerCase();
      const codeNormLc = String(codeNorm).toLowerCase();

      return (
        nameLc.includes(needle) ||
        codeLc.includes(needle) ||
        codeNormLc.includes(needle)
      );
    });
  }, [centers, q, centerNameByCode]);

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm mb-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-end">
        {/* Dates */}
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-600">
              {t("dashboard.from", "Des de")}
            </label>
            <input
              className="border rounded-xl px-3 py-2"
              type="date"
              value={local.dateFrom}
              onChange={(e) => update({ dateFrom: e.target.value })}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-600">
              {t("dashboard.to", "Fins a")}
            </label>
            <input
              className="border rounded-xl px-3 py-2"
              type="date"
              value={local.dateTo}
              onChange={(e) => update({ dateTo: e.target.value })}
            />
          </div>
        </div>

        {/* Picklist centres */}
        <div className="lg:col-span-2 relative" ref={boxRef}>
          <label className="text-xs text-gray-600">
            {t("dashboard.centers", "Centres")}
          </label>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="w-full border rounded-xl px-3 py-2 bg-white flex items-center justify-between hover:bg-gray-50 mt-1"
          >
            <span className="text-sm">
              {local.centerCodes?.length
                ? t(
                    "dashboard.centersSelected",
                    "{{count}} seleccionats",
                    { count: local.centerCodes.length }
                  )
                : t("dashboard.allCenters", "Tots els centres")}
            </span>
            <span className="text-gray-400">▾</span>
          </button>

          {open && (
            <div className="absolute z-20 mt-2 w-full bg-white border border-gray-200 rounded-2xl shadow-lg p-3">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("dashboard.searchCenters", "Cerca per nom o codi…")}
                className="w-full border rounded-xl px-3 py-2 text-sm"
              />

              <div className="mt-3 max-h-64 overflow-auto">
                {filteredCenters.map((c) => {
                  const active = local.centerCodes.includes(c.centerCode);

                  const codeNorm = normalizeCenterCode(c.centerCode);
                  const name = centerNameByCode[codeNorm] || centerNameByCode[String(c.centerCode)];
                  const label = name || String(c.centerCode);

                  return (
                    <button
                      key={c.centerCode}
                      type="button"
                      onClick={() => toggleCenter(c.centerCode)}
                      className={[
                        "w-full flex items-center justify-between px-3 py-2 rounded-xl border mb-1",
                        active
                          ? "bg-black text-white border-black"
                          : "bg-white text-gray-900 border-gray-200 hover:bg-gray-50",
                      ].join(" ")}
                    >
                      <div className="text-left min-w-0">
                        <div className="text-sm font-medium truncate">
                          {label}
                        </div>
                        <div className="text-xs opacity-70">
                          {t("dashboard.centerCode", "Codi")} {codeNorm}
                        </div>
                      </div>

                      <div className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-800">
                        {c.surveys}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="mt-3 text-right">
                <button
                  type="button"
                  className="text-sm px-3 py-2 rounded-xl bg-gray-900 text-white"
                  onClick={() => setOpen(false)}
                >
                  {t("dashboard.done", "Fet")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Chips seleccionats */}
      {selectedCenters.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {selectedCenters.map((c) => {
            const label = getCenterLabel(c.centerCode);
            return (
              <button
                key={c.centerCode}
                type="button"
                onClick={() => toggleCenter(c.centerCode)}
                className="text-xs px-3 py-1 rounded-full border border-gray-300 bg-white hover:bg-gray-50"
              >
                {label} ✕
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}